import { req } from "./transport";
import type { PublicPolicySummary, PublicPolicy, ManagedLegalKind, PreparedLegalKind, LegalWorkspace, LegalVersionSummary, LegalPublication, LegalSnapshot, LegalDraft, LegalDraftInput } from "./types";
import type { ContractTemplateKind, ContractTemplateContent, ContractTemplateDraft, ContractTemplateWorkspace } from "./types";
import type { OperationalTemplatePreview, TemplateActivation } from "./types";

export const legalApi = {
  async adminOperationalTemplatePreview(kind: ContractTemplateKind, expectedToken: string) {
    return req<OperationalTemplatePreview>(`/admin/legal/contract-templates/${kind}/operational-preview`, { method: "POST", body: JSON.stringify({ expectedToken }) }, { rejectUnauthorized: true });
  },
  async adminActivateContractTemplate(kind: ContractTemplateKind, expectedToken: string) {
    return req<{ active: TemplateActivation }>(`/admin/legal/contract-templates/${kind}/activate`, { method: "POST", body: JSON.stringify({ expectedToken }) }, { rejectUnauthorized: true });
  },
  async adminContractTemplate(kind: ContractTemplateKind) {
    return req<ContractTemplateWorkspace>(`/admin/legal/contract-templates/${kind}`, {}, { rejectUnauthorized: true });
  },
  async adminSaveContractTemplate(kind: ContractTemplateKind, content: ContractTemplateContent, expectedToken: string | null) {
    return req<{ draft: ContractTemplateDraft }>(`/admin/legal/contract-templates/${kind}`,
      { method: "PUT", body: JSON.stringify({ content, expectedToken }) }, { rejectUnauthorized: true });
  },
  // Increment 39: legal documents (ToS / Privacy) + acceptance
  async publishedPolicies() {
    return req<{ documents: PublicPolicySummary[] }>("/legal/policies", {}, { rejectUnauthorized: true });
  },

  async publishedPolicy(kind: string) {
    // Existing Terms/Privacy readers and installed clients keep the same endpoint.
    const path = kind === "terms" || kind === "privacy" ? `/legal/${kind}` : `/legal/policies/${encodeURIComponent(kind)}`;
    const response = await req<{ document: PublicPolicy }>(path, {}, { rejectUnauthorized: true });
    return response.document;
  },

  async legalDoc(kind: "terms" | "privacy") {
    const r = await req<{ document: { kind: string; version: number; title: string; body: string; publishedAt: string } }>(`/legal/${kind}`);
    return r?.document ?? null;
  },

  async legalStatus() {
    return req<{ terms: any; privacy: any; needsAcceptance: boolean }>("/legal/me/status");
  },

  async acceptLegal(kinds?: Array<"terms" | "privacy">) {
    return req<{ ok: boolean; accepted: any[]; status: any }>("/legal/accept", { method: "POST", body: JSON.stringify({ kinds }) });
  },

  async adminLegal() {
    return req<{ current: Record<string, number>; documents: LegalVersionSummary[] }>("/admin/legal", {}, { rejectUnauthorized: true });
  },

  async adminLegalDoc(id: string) {
    const r = await req<{ document: LegalPublication }>(`/admin/legal/${encodeURIComponent(id)}`, {}, { rejectUnauthorized: true });
    return r.document;
  },

  async adminLegalWorkspace(kind: ManagedLegalKind) {
    return req<LegalWorkspace>(`/admin/legal/workspace/${kind}`, {}, { rejectUnauthorized: true });
  },
  async adminPreparedLegalDraft(kind: PreparedLegalKind) {
    return req<{ document: { title: string; description: string; body: string; revision: string } }>(`/admin/legal/review-drafts/${kind}`, {}, { rejectUnauthorized: true });
  },
  async adminLegalDraft(kind: ManagedLegalKind, token: string) {
    const response = await req<{ document: LegalSnapshot }>(`/admin/legal/drafts/${kind}?token=${encodeURIComponent(token)}`, {}, { rejectUnauthorized: true });
    return response.document;
  },
  async adminSaveLegalDraft(kind: ManagedLegalKind, input: LegalDraftInput) {
    return req<{ draft: LegalDraft }>(`/admin/legal/drafts/${kind}`, { method: "PUT", body: JSON.stringify(input) }, { rejectUnauthorized: true });
  },
  async adminPublishLegalDraft(kind: ManagedLegalKind, token: string) {
    return req<{ ok: boolean; document: LegalPublication }>(`/admin/legal/drafts/${kind}/publish`, { method: "POST", body: JSON.stringify({ token }) }, { rejectUnauthorized: true });
  },

  async adminPublishLegal(input: { kind: "terms" | "privacy"; title: string; body: string; publish?: boolean }) {
    return req<{ ok: boolean; document: LegalPublication }>("/admin/legal", { method: "POST", body: JSON.stringify(input) }, { rejectUnauthorized: true });
  }
};
